import React from 'react';
import { useTranslation } from 'react-i18next';
import { SoilRound } from '../../services/nutritionService';
import { naturalCompare } from '../../utils/naturalCompare';
import './nutrition.css';

export interface NutritionMembershipPickerProps {
  round: SoilRound;
  /** orchardNos (foliar) / groupIds (soil) already having a live session — hidden. */
  unavailableIds?: string[];
  /** Controlled selection (orchardNos or groupIds), already limited to available ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /**
   * data-testid for the select-all checkbox. Defaults to 'membership-select-all';
   * callers embedding this picker in a component with its own established
   * test contract (e.g. NutritionConvertRoundModal's 'convert-select-all')
   * can override it to avoid breaking existing tests.
   */
  selectAllTestId?: string;
}

/** Flatten unique orchardIds from all groups in a round. */
export function flattenOrchards(round: SoilRound): string[] {
  const seen = new Set<string>();
  (round.groups || []).forEach((g) => {
    (g.orchardIds || []).forEach((id) => seen.add(id));
  });
  return Array.from(seen).sort(naturalCompare);
}

const NutritionMembershipPicker: React.FC<NutritionMembershipPickerProps> = ({
  round,
  unavailableIds,
  value,
  onChange,
  selectAllTestId = 'membership-select-all',
}) => {
  const { t } = useTranslation();
  const isSoil = round.type === 'soil';
  const unavailable = new Set(unavailableIds || []);

  const allOrchards = isSoil ? [] : flattenOrchards(round);
  const allGroups = isSoil ? (round.groups || []) : [];
  const orchards = allOrchards.filter((o) => !unavailable.has(o));
  const groups = allGroups.filter((g) => !unavailable.has(g.groupId));
  const items: string[] = isSoil ? groups.map((g) => g.groupId) : orchards;

  const foliarAllTaken = !isSoil && allOrchards.length > 0 && orchards.length === 0;
  const soilAllTaken = isSoil && allGroups.length > 0 && groups.length === 0;

  const selected = new Set(value.filter((id) => items.includes(id)));
  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0 && selected.size < items.length;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(items.filter((i) => next.has(i)));
  };
  const toggleAll = () => onChange(allSelected ? [] : [...items]);

  if (items.length === 0) {
    const key = isSoil
      ? (soilAllTaken ? 'nutrition.sessions.convertModal.allGroupsConverted' : 'nutrition.sessions.convertModal.noGroups')
      : (foliarAllTaken ? 'nutrition.sessions.convertModal.allOrchardsConverted' : 'nutrition.sessions.convertModal.noOrchards');
    return <div className="ntr-muted" style={{ fontSize: 13 }}>{t(key)}</div>;
  }

  return (
    <div className="ntr-bulk-list">
      <label className="ntr-bulk-row ntr-bulk-row--all" style={{ cursor: 'pointer' }}>
        <input
          type="checkbox"
          className="pat-checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleAll}
          aria-label={t('nutrition.sessions.convertModal.selectAll')}
          data-testid={selectAllTestId}
        />
        <span>
          {allSelected
            ? t('nutrition.sessions.convertModal.deselectAll')
            : t('nutrition.sessions.convertModal.selectAll')}
        </span>
      </label>
      {isSoil
        ? groups.map((group) => {
            const count = (group.orchardIds || []).length;
            const label = group.label || group.groupId;
            return (
              <label key={group.groupId} className="ntr-bulk-row" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  className="pat-checkbox"
                  checked={selected.has(group.groupId)}
                  onChange={() => toggle(group.groupId)}
                  aria-label={label}
                />
                <span>
                  {label}{' '}
                  <span className="ntr-muted" style={{ fontSize: 12 }}>
                    {t('nutrition.sessions.convertModal.groupOrchards', { count })}
                  </span>
                </span>
              </label>
            );
          })
        : orchards.map((orchardNo) => (
            <label key={orchardNo} className="ntr-bulk-row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="pat-checkbox"
                checked={selected.has(orchardNo)}
                onChange={() => toggle(orchardNo)}
                aria-label={orchardNo}
              />
              <span>{orchardNo}</span>
            </label>
          ))}
    </div>
  );
};

export default NutritionMembershipPicker;
